import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  OnDestroy,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject, forkJoin, of } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';
import * as L from 'leaflet';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { LoaderService } from '@/app/common/services/loader/loader.service';
import {
  ChecklistHeader,
  HeaderTypeEnum,
} from '@/app/modules/checklist-header/core/type';
import { ChecklistHeaderService } from '@/app/modules/checklist-header/core/service';
import { VehicleTrackerService } from '@/app/modules/vehicle-tracker/core/service';
import {
  TRACKER_POSITION_TOPIC,
  VehicleTrackerEnrichment,
  VehicleTrackerPosition,
  VehicleTrackerRealtimePosition,
  VehicleTrackerSummary,
} from '@/app/modules/vehicle-tracker/core/type';
import {
  buildTrackerEnrichmentMap,
  normalizeTrackerChassis,
} from '@/app/modules/vehicle-tracker/core/utils';
import {
  ConnectionStatus,
  WebsocketService,
} from '@/app/modules/websocket/core/service';
import { environment } from '@/environments/environment';

const DEFAULT_CENTER: L.LatLngExpression = [-15.601, -56.097];
const DEFAULT_ZOOM = 5;
const SINGLE_MARKER_ZOOM = 14;
const POSITIONS_PAGE_SIZE = 500;
const CHECKLISTS_PAGE_SIZE = 10000;
const POPUP_CLOSE_DELAY_MS = 280;

type MotionFilter = 'all' | 'moving' | 'stopped';

interface TrackerListItem {
  chassis: string;
  speed: number;
  ignition: string | null | undefined;
  recordedAt: string;
  updatedLabel: string;
  moving: boolean;
  responsibleName: string | null;
  productName: string | null;
}

@Component({
  selector: 'app-vehicle-tracker-map',
  standalone: true,
  templateUrl: './map.component.html',
  styleUrls: ['./map.component.scss'],
  imports: [
    CommonModule,
    FormsModule,
    NzAlertModule,
    NzButtonModule,
    NzEmptyModule,
    NzIconModule,
    NzInputModule,
    NzSelectModule,
    NzSpinModule,
    NzSwitchModule,
    NzToolTipModule,
  ],
})
export class VehicleTrackerMapComponent implements AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: true })
  mapContainer!: ElementRef<HTMLDivElement>;

  loading = false;
  refreshing = false;
  socketStatus: ConnectionStatus = 'disconnected';
  errorMessage: string | null = null;
  checklistWarning: string | null = null;
  summary: VehicleTrackerSummary | null = null;
  apiPositionsTotal = 0;
  mappedCount = 0;
  filteredMovingCount = 0;
  filteredStoppedCount = 0;
  mapReady = false;
  selectedChassis: string | null = null;
  searchTerm = '';
  motionFilter: MotionFilter = 'all';
  ignitionOnOnly = false;
  listItems: TrackerListItem[] = [];
  readonly motionFilterOptions: { label: string; value: MotionFilter }[] = [
    { label: 'Todos', value: 'all' },
    { label: 'Em movimento', value: 'moving' },
    { label: 'Parados', value: 'stopped' },
  ];

  private map?: L.Map;
  private markersLayer = L.layerGroup();
  private markersByChassis = new Map<string, L.Marker>();
  private viewReady = false;
  private pendingRender: VehicleTrackerPosition[] | null = null;
  private lastBounds: L.LatLngBounds | null = null;
  private resizeObserver?: ResizeObserver;
  private enrichmentByChassis = new Map<string, VehicleTrackerEnrichment>();
  private popupCloseTimer: ReturnType<typeof setTimeout> | null = null;
  private activePopupMarker: L.Marker | null = null;
  private fleetPositions: VehicleTrackerPosition[] = [];
  private destroy$ = new Subject<void>();
  private readonly onRealtimePosition = (data: unknown) => {
    this.applyRealtimePosition(data as VehicleTrackerRealtimePosition);
  };

  constructor(
    private vehicleTrackerService: VehicleTrackerService,
    private checklistHeaderService: ChecklistHeaderService,
    private loaderService: LoaderService,
    private websocketService: WebsocketService,
    private router: Router,
  ) {}

  ngAfterViewInit(): void {
    this.initMap();
    this.viewReady = true;
    this.loadMapData(true);
    this.bindRealtime();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.websocketService.removeListener(TRACKER_POSITION_TOPIC);
    this.clearPopupCloseTimer();
    this.resizeObserver?.disconnect();
    this.map?.remove();
    this.map = undefined;
  }

  loadMapData(showLoader = true): void {
    if (showLoader) {
      this.loading = true;
    } else {
      this.refreshing = true;
    }
    this.errorMessage = null;
    if (showLoader) {
      this.checklistWarning = null;
    }

    const request$ = forkJoin({
      summary: this.vehicleTrackerService.getSummary(),
      positions: this.vehicleTrackerService.findAllPositions({
        pageIndex: 1,
        pageSize: POSITIONS_PAGE_SIZE,
      }),
      checklists: this.checklistHeaderService
        .findAll({
          pageIndex: 1,
          pageSize: CHECKLISTS_PAGE_SIZE,
          type: HeaderTypeEnum.VEHICLE,
        })
        .pipe(
          catchError(() => {
            if (showLoader) {
              this.checklistWarning =
                'Não foi possível carregar os checklists de veículo. O popup ficará só com telemetria.';
            }
            return of({
              items: [] as ChecklistHeader[],
              total: 0,
            });
          }),
        ),
    });

    const source$ = showLoader
      ? this.loaderService.executeWithLoader(request$)
      : request$;

    source$.pipe(takeUntil(this.destroy$)).subscribe({
      next: ({ summary, positions, checklists }) => {
        this.summary = summary;
        this.apiPositionsTotal = positions.total;
        this.enrichmentByChassis = buildTrackerEnrichmentMap(checklists.items);
        this.fleetPositions = this.filterValidPositions(positions.items);
        this.applyFiltersAndRender(!showLoader);
        this.loading = false;
        this.refreshing = false;
        this.scheduleInvalidateSize();
      },
      error: (error) => {
        this.loading = false;
        this.refreshing = false;
        if (showLoader) {
          this.mappedCount = 0;
          this.listItems = [];
          this.clearMarkers();
          this.lastBounds = null;
          this.fleetPositions = [];
          this.errorMessage =
            error?.error?.message ||
            'Não foi possível carregar os dados do rastreador.';
        }
        this.scheduleInvalidateSize();
      },
    });
  }

  onFiltersChange(): void {
    this.applyFiltersAndRender(true);
  }

  clearFilters(): void {
    this.searchTerm = '';
    this.motionFilter = 'all';
    this.ignitionOnOnly = false;
    this.onFiltersChange();
  }

  fitFleet(): void {
    if (!this.map || !this.lastBounds || this.mappedCount === 0) {
      return;
    }

    if (this.mappedCount === 1) {
      this.map.setView(this.lastBounds.getCenter(), SINGLE_MARKER_ZOOM);
      return;
    }

    this.map.fitBounds(this.lastBounds, { padding: [48, 48], maxZoom: 14 });
  }

  focusVehicle(chassis: string): void {
    const previous = this.selectedChassis;
    const normalized = normalizeTrackerChassis(chassis);
    this.selectedChassis = normalized;
    this.refreshMarkerSelection(previous, normalized);

    const marker = this.markersByChassis.get(normalized);
    if (!marker || !this.map) {
      return;
    }

    const latLng = marker.getLatLng();
    this.map.flyTo(latLng, Math.max(this.map.getZoom(), 13), {
      duration: 0.6,
    });
    this.clearPopupCloseTimer();
    if (this.activePopupMarker && this.activePopupMarker !== marker) {
      this.activePopupMarker.closePopup();
    }
    this.activePopupMarker = marker;
    marker.openPopup();
    this.attachPopupHoverGuards(marker);
  }

  openDetail(chassis: string, event?: Event): void {
    event?.stopPropagation();
    const normalized = normalizeTrackerChassis(chassis);
    if (!normalized) {
      return;
    }
    this.router.navigate(['/tracker', normalized]);
  }

  private refreshMarkerSelection(
    previous: string | null,
    next: string | null,
  ): void {
    for (const chassis of [previous, next]) {
      if (!chassis) {
        continue;
      }
      const marker = this.markersByChassis.get(chassis);
      const position = this.fleetPositions.find(
        (p) => normalizeTrackerChassis(p.chassis) === chassis,
      );
      if (!marker || !position) {
        continue;
      }
      marker.setIcon(
        this.createMarkerIcon(chassis, (position.speed ?? 0) > 0),
      );
    }
  }

  trackByChassis(_: number, item: TrackerListItem): string {
    return item.chassis;
  }

  get liveStatusLabel(): string {
    switch (this.socketStatus) {
      case 'connected':
        return 'Ao vivo';
      case 'connecting':
        return 'Conectando…';
      default:
        return 'Offline';
    }
  }

  private bindRealtime(): void {
    this.websocketService.addListener(
      TRACKER_POSITION_TOPIC,
      this.onRealtimePosition,
    );

    this.websocketService.connectionStatus
      .pipe(takeUntil(this.destroy$))
      .subscribe((status) => {
        // Defer past the current CD cycle to avoid NG0100 on the live tag.
        setTimeout(() => {
          this.socketStatus = status;
          if (status === 'connected') {
            this.websocketService.addListener(
              TRACKER_POSITION_TOPIC,
              this.onRealtimePosition,
            );
          }
        }, 0);
      });

    this.websocketService.connect();
  }

  private applyRealtimePosition(payload: VehicleTrackerRealtimePosition): void {
    if (!payload?.chassis) {
      return;
    }

    const chassis = normalizeTrackerChassis(payload.chassis);
    if (!chassis) {
      return;
    }

    if (
      !Number.isFinite(payload.latitude) ||
      !Number.isFinite(payload.longitude) ||
      (payload.latitude === 0 && payload.longitude === 0)
    ) {
      return;
    }

    const next: VehicleTrackerPosition = {
      chassis,
      latitude: payload.latitude,
      longitude: payload.longitude,
      speed: payload.speed ?? 0,
      direction: payload.direction ?? null,
      ignition: payload.ignition ?? null,
      satelliteCount: payload.satelliteCount ?? null,
      batteryLevel: payload.batteryLevel ?? null,
      trackerBatteryLevel: payload.trackerBatteryLevel ?? null,
      temperature: payload.temperature ?? null,
      engineRpm: payload.engineRpm ?? null,
      recordedAt: payload.recordedAt,
      receivedAt: payload.receivedAt,
    };

    const index = this.fleetPositions.findIndex(
      (item) => normalizeTrackerChassis(item.chassis) === chassis,
    );

    if (index >= 0) {
      this.fleetPositions[index] = next;
    } else {
      this.fleetPositions = [...this.fleetPositions, next];
      this.apiPositionsTotal = Math.max(
        this.apiPositionsTotal,
        this.fleetPositions.length,
      );
    }

    if (this.summary) {
      const moving = this.fleetPositions.filter((p) => (p.speed ?? 0) > 0).length;
      this.summary = {
        ...this.summary,
        totalVehicles: this.fleetPositions.length,
        inMotion: moving,
        stopped: this.fleetPositions.length - moving,
        ignitionOn: this.fleetPositions.filter((p) => p.ignition === 'L').length,
        lastUpdate: payload.receivedAt || payload.recordedAt,
      };
    }

    this.applyFiltersAndRender(true);
  }

  private filterValidPositions(
    positions: VehicleTrackerPosition[],
  ): VehicleTrackerPosition[] {
    return positions.filter(
      (p) =>
        Number.isFinite(p.latitude) &&
        Number.isFinite(p.longitude) &&
        !(p.latitude === 0 && p.longitude === 0),
    );
  }

  private applyFiltersAndRender(preserveView: boolean): void {
    const filtered = this.fleetPositions.filter((position) =>
      this.matchesFilters(position),
    );

    this.mappedCount = filtered.length;
    this.filteredMovingCount = filtered.filter((p) => (p.speed ?? 0) > 0).length;
    this.filteredStoppedCount = filtered.length - this.filteredMovingCount;
    this.listItems = filtered
      .map((position) => this.toListItem(position))
      .sort((a, b) => a.chassis.localeCompare(b.chassis));

    this.syncMarkers(filtered, preserveView);
  }

  private matchesFilters(position: VehicleTrackerPosition): boolean {
    const chassis = normalizeTrackerChassis(position.chassis);
    const term = normalizeTrackerChassis(this.searchTerm);
    if (term && !chassis.includes(term)) {
      return false;
    }

    const speed = position.speed ?? 0;
    if (this.motionFilter === 'moving' && speed <= 0) {
      return false;
    }
    if (this.motionFilter === 'stopped' && speed > 0) {
      return false;
    }
    if (this.ignitionOnOnly && position.ignition !== 'L') {
      return false;
    }

    return true;
  }

  private toListItem(position: VehicleTrackerPosition): TrackerListItem {
    const chassis = normalizeTrackerChassis(position.chassis);
    const enrichment = this.enrichmentByChassis.get(chassis);
    return {
      chassis,
      speed: position.speed ?? 0,
      ignition: position.ignition,
      recordedAt: position.recordedAt,
      updatedLabel: this.formatRelativeTime(position.recordedAt),
      moving: (position.speed ?? 0) > 0,
      responsibleName: enrichment?.responsibleName ?? null,
      productName: enrichment?.productName ?? null,
    };
  }

  private initMap(): void {
    if (this.map || !this.mapContainer?.nativeElement) {
      return;
    }

    this.map = L.map(this.mapContainer.nativeElement, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      zoomControl: true,
    });

    const { urlTemplate, attribution, maxZoom } = environment.mapTiles;
    L.tileLayer(urlTemplate, { attribution, maxZoom }).addTo(this.map);

    this.markersLayer.addTo(this.map);
    this.mapReady = true;

    this.resizeObserver = new ResizeObserver(() => {
      this.map?.invalidateSize({ animate: false });
    });
    this.resizeObserver.observe(this.mapContainer.nativeElement);

    this.scheduleInvalidateSize();

    if (this.pendingRender) {
      const pending = this.pendingRender;
      this.pendingRender = null;
      this.syncMarkers(pending, false);
    }
  }

  private syncMarkers(
    positions: VehicleTrackerPosition[],
    preserveView: boolean,
  ): void {
    if (!this.map || !this.viewReady) {
      this.pendingRender = positions;
      return;
    }

    const nextKeys = new Set(
      positions.map((p) => normalizeTrackerChassis(p.chassis)),
    );

    for (const [chassis, marker] of this.markersByChassis) {
      if (!nextKeys.has(chassis)) {
        this.markersLayer.removeLayer(marker);
        this.markersByChassis.delete(chassis);
        if (this.activePopupMarker === marker) {
          this.activePopupMarker = null;
        }
        if (this.selectedChassis === chassis) {
          this.selectedChassis = null;
        }
      }
    }

    const bounds = L.latLngBounds([]);

    for (const position of positions) {
      const chassis = normalizeTrackerChassis(position.chassis);
      const latLng: L.LatLngExpression = [
        position.latitude,
        position.longitude,
      ];
      const moving = (position.speed ?? 0) > 0;
      const enrichment = this.enrichmentByChassis.get(chassis);
      let marker = this.markersByChassis.get(chassis);

      if (!marker) {
        marker = L.marker(latLng, {
          icon: this.createMarkerIcon(chassis, moving),
          riseOnHover: true,
          zIndexOffset: moving ? 200 : 100,
        });
        marker.bindPopup(this.buildPopupHtml(position, enrichment), {
          className: 'tracker-vehicle-popup',
          closeButton: false,
          offset: L.point(0, -40),
          maxWidth: 280,
          autoPan: false,
        });
        this.bindPopupHover(marker, chassis);
        this.markersLayer.addLayer(marker);
        this.markersByChassis.set(chassis, marker);
      } else {
        marker.setLatLng(latLng);
        marker.setIcon(this.createMarkerIcon(chassis, moving));
        marker.setZIndexOffset(moving ? 200 : 100);
        marker.setPopupContent(this.buildPopupHtml(position, enrichment));
      }

      bounds.extend(latLng);
    }

    this.lastBounds = positions.length ? bounds : null;

    if (!preserveView) {
      if (!positions.length) {
        this.map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
      } else if (positions.length === 1) {
        this.map.setView(bounds.getCenter(), SINGLE_MARKER_ZOOM);
      } else {
        this.map.fitBounds(bounds, { padding: [48, 48], maxZoom: 14 });
      }
    }

    this.scheduleInvalidateSize();
  }

  private bindPopupHover(marker: L.Marker, chassis: string): void {
    marker.on('mouseover', () => {
      const previous = this.selectedChassis;
      this.selectedChassis = chassis;
      this.refreshMarkerSelection(previous, chassis);
      this.clearPopupCloseTimer();
      if (this.activePopupMarker && this.activePopupMarker !== marker) {
        this.activePopupMarker.closePopup();
      }
      this.activePopupMarker = marker;
      marker.openPopup();
      this.attachPopupHoverGuards(marker);
    });

    marker.on('mouseout', () => {
      this.schedulePopupClose(marker);
    });

    marker.on('click', () => {
      this.focusVehicle(chassis);
    });
  }

  private attachPopupHoverGuards(marker: L.Marker): void {
    const popup = marker.getPopup();
    const el = popup?.getElement();
    if (
      !el ||
      (el as HTMLElement & { __trackerHoverBound?: boolean }).__trackerHoverBound
    ) {
      return;
    }

    (el as HTMLElement & { __trackerHoverBound?: boolean }).__trackerHoverBound =
      true;

    el.addEventListener('mouseenter', () => {
      this.clearPopupCloseTimer();
    });
    el.addEventListener('mouseleave', () => {
      this.schedulePopupClose(marker);
    });
    el.addEventListener('click', (event) => {
      const target = (event.target as HTMLElement | null)?.closest(
        '[data-tracker-detail]',
      );
      if (!target) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      this.openDetail(target.getAttribute('data-tracker-detail') || '');
    });
  }

  private schedulePopupClose(marker: L.Marker): void {
    this.clearPopupCloseTimer();
    this.popupCloseTimer = setTimeout(() => {
      if (this.activePopupMarker === marker) {
        marker.closePopup();
        this.activePopupMarker = null;
      }
    }, POPUP_CLOSE_DELAY_MS);
  }

  private clearPopupCloseTimer(): void {
    if (this.popupCloseTimer) {
      clearTimeout(this.popupCloseTimer);
      this.popupCloseTimer = null;
    }
  }

  private createMarkerIcon(chassis: string, moving: boolean): L.DivIcon {
    const stateClass = moving ? ' tracker-marker--moving' : '';
    const selectedClass =
      this.selectedChassis === chassis ? ' tracker-marker--selected' : '';
    const plate = this.escapeHtml(chassis);

    return L.divIcon({
      className: 'tracker-marker',
      html: `
        <div class="tracker-marker__pin${stateClass}${selectedClass}">
          <span class="tracker-marker__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
              <path d="M18.92 6.01C18.72 5.42 18.16 5 17.5 5h-11c-.66 0-1.21.42-1.42 1.01L3 12v8c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-1h12v1c0 .55.45 1 1 1h1c.55 0 1-.45 1-1v-8l-2.08-5.99zM6.5 16c-.83 0-1.5-.67-1.5-1.5S5.67 13 6.5 13s1.5.67 1.5 1.5S7.33 16 6.5 16zm11 0c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zM5 11l1.5-4.5h11L19 11H5z"/>
            </svg>
          </span>
          <span class="tracker-marker__plate">${plate}</span>
        </div>
      `,
      iconSize: [88, 52],
      iconAnchor: [44, 46],
      popupAnchor: [0, -46],
    });
  }

  private buildPopupHtml(
    position: VehicleTrackerPosition,
    enrichment?: VehicleTrackerEnrichment,
  ): string {
    const speed = Number.isFinite(position.speed) ? position.speed : 0;
    const status = speed > 0 ? 'Em movimento' : 'Parado';
    const ignition = this.formatIgnition(position.ignition);
    const updated = this.formatRecordedAt(position.recordedAt);
    const km =
      enrichment?.hoursMeter !== null && enrichment?.hoursMeter !== undefined
        ? `${this.formatNumber(enrichment.hoursMeter)} km`
        : '—';
    const responsible = enrichment?.responsibleName || '—';
    const model = enrichment?.productName || '—';

    return `
      <div class="tracker-popup">
        <div class="tracker-popup__header">
          <span class="tracker-popup__plate">${this.escapeHtml(position.chassis)}</span>
          <span class="tracker-popup__status tracker-popup__status--${speed > 0 ? 'moving' : 'stopped'}">${status}</span>
        </div>
        <div class="tracker-popup__grid">
          <div class="tracker-popup__item">
            <span class="tracker-popup__label">Última atualização</span>
            <span class="tracker-popup__value">${this.escapeHtml(updated)}</span>
          </div>
          <div class="tracker-popup__item">
            <span class="tracker-popup__label">Velocidade</span>
            <span class="tracker-popup__value">${speed} km/h</span>
          </div>
          <div class="tracker-popup__item">
            <span class="tracker-popup__label">Ignição</span>
            <span class="tracker-popup__value">${this.escapeHtml(ignition)}</span>
          </div>
          <div class="tracker-popup__item">
            <span class="tracker-popup__label">Km (checklist)</span>
            <span class="tracker-popup__value">${this.escapeHtml(km)}</span>
          </div>
          <div class="tracker-popup__item tracker-popup__item--full">
            <span class="tracker-popup__label">Responsável</span>
            <span class="tracker-popup__value">${this.escapeHtml(responsible)}</span>
          </div>
          <div class="tracker-popup__item tracker-popup__item--full">
            <span class="tracker-popup__label">Modelo</span>
            <span class="tracker-popup__value">${this.escapeHtml(model)}</span>
          </div>
        </div>
        <a
          href="/tracker/${this.escapeHtml(position.chassis)}"
          class="tracker-popup__link"
          data-tracker-detail="${this.escapeHtml(position.chassis)}"
        >Ver detalhe</a>
      </div>
    `;
  }

  private formatIgnition(ignition?: string | null): string {
    if (ignition === 'L') {
      return 'Ligada';
    }
    if (ignition === 'D') {
      return 'Desligada';
    }
    return 'Desconhecida';
  }

  private formatRecordedAt(value: string): string {
    const relative = this.formatRelativeTime(value);
    if (relative === '—') {
      return '—';
    }

    const date = new Date(value);
    const absolute = date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });

    return `${relative} · ${absolute}`;
  }

  private formatRelativeTime(value: string): string {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '—';
    }

    const abs = Math.abs(Date.now() - date.getTime());
    const minutes = Math.floor(abs / 60_000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (minutes < 1) {
      return 'agora';
    }
    if (minutes < 60) {
      return `há ${minutes} min`;
    }
    if (hours < 24) {
      return `há ${hours} h`;
    }
    return `há ${days} d`;
  }

  private formatNumber(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      maximumFractionDigits: 0,
    }).format(value);
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  private clearMarkers(): void {
    this.markersLayer.clearLayers();
    this.markersByChassis.clear();
  }

  private scheduleInvalidateSize(): void {
    setTimeout(() => this.map?.invalidateSize({ animate: false }), 0);
  }
}
